import { useMemo } from "react";
import { Version } from "../../types/version";
import { SYSTEM_LANGUAGE_LABELS } from "../../shared/enums/shared-enums";
import { SystemLanguages } from "../../shared/enums/shared-enums";
import { initMetadata } from "../../controllers/formController";
import { SystemEntityProofreadingStatus } from "../../shared/enums/shared-enums";
import { DEFAULT_INTELLEXUS_DATE } from "../../types/intellexusDate";
import { MRT_ColumnDef } from "material-react-table";
import {
  chineseVersionColumns,
  indicVersionColumns,
  tibetanVersionColumns,
} from "../../common/entities/VersionColumns";
import useEvidenceLogic from "./evidenceLogic";

const VERSION_COLLECTION_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.indic]: "indic_version_collection",
  [SystemLanguages.tibetan]: "tibetan_version_collection",
} as const;

const VERSION_LINK_ENUM_MAP: Partial<Record<SystemLanguages, string>> = {
  [SystemLanguages.indic]: "indic_version_links",
  [SystemLanguages.tibetan]: "tibetan_version_links",
  [SystemLanguages.chinese]: "chinese_version_links",
} as const;

const useVersionLogic = (language: string | null | undefined) => {
  const systemLanguageType = useMemo<
    Version["system_language_type"] | null
  >(() => {
    if (!Object.values(SystemLanguages).includes(language as SystemLanguages)) {
      return null;
    }
    return language as Version["system_language_type"];
  }, [language]);

  const languageBasedColumns = useMemo<MRT_ColumnDef<Version, any>[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return tibetanVersionColumns;
      case SystemLanguages.indic:
        return indicVersionColumns;
      case SystemLanguages.chinese:
        return chineseVersionColumns;
    }
  }, [systemLanguageType]);

  const showTibetanFields = useMemo<boolean>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType === SystemLanguages.tibetan;
  }, [systemLanguageType]);

  const versionSourceLanguageEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return `version_source_language`;
  }, [systemLanguageType]);

  const versionCollectionEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return VERSION_COLLECTION_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const completenessEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "completeness";
  }, [systemLanguageType]);

  const { internalEvidenceTypeEnum, externalEvidenceTypeEnum } =
    useEvidenceLogic();

  const versionLinkToOtherDatabasesEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return VERSION_LINK_ENUM_MAP[systemLanguageType] || "";
  }, [systemLanguageType]);

  const transferedCollectionEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "tibetan_transfered_collection";
  }, [systemLanguageType]);

  const doxographicalCategoryEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return "section_doxographical_category";
  }, [systemLanguageType]);

  const languageEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType === SystemLanguages.indic
      ? "indic_version_language"
      : "";
  }, [systemLanguageType]);

  const statusOfIndicOriginEnum = useMemo<string>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return systemLanguageType === SystemLanguages.indic
      ? "status_of_indic_origin"
      : "";
  }, [systemLanguageType]);

  const userEnums = useMemo<string[]>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.tibetan:
        return [
          versionSourceLanguageEnum,
          versionCollectionEnum,
          completenessEnum,
          versionLinkToOtherDatabasesEnum,
          transferedCollectionEnum,
          doxographicalCategoryEnum,
          internalEvidenceTypeEnum,
          externalEvidenceTypeEnum,
        ];
      case SystemLanguages.indic:
        return [
          completenessEnum,
          versionLinkToOtherDatabasesEnum,
          doxographicalCategoryEnum,
          languageEnum,
          statusOfIndicOriginEnum,
          internalEvidenceTypeEnum,
          externalEvidenceTypeEnum,
        ];
      case SystemLanguages.chinese:
        return [completenessEnum, versionLinkToOtherDatabasesEnum];
    }
  }, [
    systemLanguageType,
    versionSourceLanguageEnum,
    versionCollectionEnum,
    completenessEnum,
    versionLinkToOtherDatabasesEnum,
    transferedCollectionEnum,
    doxographicalCategoryEnum,
    languageEnum,
    statusOfIndicOriginEnum,
    internalEvidenceTypeEnum,
    externalEvidenceTypeEnum,
  ]);

  const metadataFieldNames = useMemo<Array<keyof Version>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return [
      "name",
      "completeness",
      "source_language",
      "version_date",
      "collection",
      "vol_number",
      "vol_name",
      "folio_start",
      "folio_end",
      "notes",
      "catalog",
      "transfered_collection",
      "doxographical_category",
      "language",
      "status_of_indic_origin",
    ];
  }, [systemLanguageType]);

  const languageBasedFields = useMemo<Array<keyof Version>>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    switch (systemLanguageType) {
      case SystemLanguages.indic:
        return [
          "title",
          "doxographical_category",
          "language",
          "status_of_indic_origin",
          "link_to_other_databases",
          "date_of_composition",
        ];
      case SystemLanguages.tibetan:
        return [
          "title",
          "catalog",
          "transfered_collection",
          "doxographical_category",
          "link_to_other_databases",
          "date_of_composition",
        ];
      case SystemLanguages.chinese:
        return ["link_to_other_databases", "date_of_composition"];
    }
  }, [systemLanguageType]);

  const showField = (field: keyof Version): boolean => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return languageBasedFields.includes(field);
  };

  const defaultVersion = useMemo<Version>(() => {
    if (!systemLanguageType) throw new Error("Invalid system language type");
    return {
      completeness: "",
      modified_by: "",
      _rev: "",
      name: "",
      notes: "",
      published_id: "",
      source_language: "",
      version_date: DEFAULT_INTELLEXUS_DATE,
      collection: "",
      vol_number: "",
      vol_name: "",
      folio_start: "",
      folio_end: "",
      title: [],
      catalog: { catalog_no: "", catalog_entry: "" },
      transfered_collection: "",
      doxographical_category: "",
      language: [],
      status_of_indic_origin: [],
      link_to_other_databases: [],
      date_of_composition: DEFAULT_INTELLEXUS_DATE,
      _id: "",
      _key: "",
      system_language_type: systemLanguageType,
      work_id: null,
      work: null,
      attributes_metadata: initMetadata<Version>(metadataFieldNames),
      internal_notes: "",
      entity_status_of_proofreading:
        SystemEntityProofreadingStatus.NOT_PROOFREAD,
    };
  }, [systemLanguageType, metadataFieldNames]);

  const getDisplayName = (version: Version): string => {
    const systemLanguageLabel =
      SYSTEM_LANGUAGE_LABELS[version.system_language_type];
    const prefix = systemLanguageLabel
      ? `${systemLanguageLabel} Version`
      : "Version";
    const displayName =
      (version.name || version.published_id ? `: ` : "") +
      version.published_id +
      (version.published_id && version.name ? " - " : "") +
      version.name;
    return `${prefix}${displayName}`;
  };

  return {
    systemLanguageType,
    languageBasedColumns,
    userEnums,
    metadataFieldNames,
    defaultVersion,
    getDisplayName,
    versionSourceLanguageEnum,
    versionCollectionEnum,
    completenessEnum,
    versionLinkToOtherDatabasesEnum,
    transferedCollectionEnum,
    doxographicalCategoryEnum,
    languageEnum,
    statusOfIndicOriginEnum,
    showTibetanFields,
    showField,
  };
};

export default useVersionLogic;
